import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../utils/toast';

import { getEventDashboardStats } from '../../services/api';
import { EventDashboardStatsDto } from '../../types/types';
import { EmptyState } from './emptyState';
import { Skeleton } from './skeleton';

interface StatCardConfig {
  titleKey: string;
  value: (stats: EventDashboardStatsDto) => number;
  cardClass: string;
  valueClass: string;
}

const STAT_CARDS: StatCardConfig[] = [
  {
    titleKey: 'total_events',
    value: (stats) => stats.totalEvents,
    cardClass: 'bg-slate-50',
    valueClass: 'text-slate-600',
  },
  {
    titleKey: 'draft_events',
    value: (stats) => stats.draftEvents,
    cardClass: 'bg-orange-50',
    valueClass: 'text-orange-600',
  },
  {
    titleKey: 'active_events',
    value: (stats) => stats.activeEvents,
    cardClass: 'bg-blue-50',
    valueClass: 'text-blue-600',
  },
  {
    titleKey: 'events_completed',
    value: (stats) => stats.completedEvents,
    cardClass: 'bg-green-50',
    valueClass: 'text-green-600',
  },
];

export const EventStatsCards: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const [stats, setStats] = useState<EventDashboardStatsDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isCancelled = false;

    getEventDashboardStats()
      .then((data) => {
        if (!isCancelled) setStats(data);
      })
      .catch(() => {
        if (!isCancelled) toast.error(t('failed_to_load_event_stats'));
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [t]);

  const isEmpty = stats !== null && stats.totalEvents === 0;

  return (
    <div className="grid h-full grid-cols-1 gap-6 sm:grid-cols-2 font-['Plus_Jakarta_Sans',sans-serif]">
      {STAT_CARDS.map(({ titleKey, value, cardClass, valueClass }) => (
        <div
          key={titleKey}
          className={`flex min-w-0 flex-col gap-4 rounded-3xl p-4 shadow-sm ring-1 ring-black/5 ${cardClass}`}
        >
          <p className="text-sm font-medium leading-5 text-gray-900">{t(titleKey)}</p>
          {isLoading ? (
            <Skeleton className="h-10 w-20" />
          ) : isEmpty ? (
            <EmptyState text={t('no_data_to_display')} iconClassName="h-12 w-12" />
          ) : (
            <p className={`text-4xl font-extrabold leading-10 ${valueClass}`}>
              {stats ? value(stats) : '—'}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};
